<script setup>
// Auto-extracted chrome for EignungsAmpel.vue — header + footer copied verbatim
// from Home so legal pages (Impressum/Datenschutz/AGB) render with the
// pilot's own masthead + footer (no AppLayout chrome leak).
// Body markup of the wrapper is yielded via <slot />.
import { computed } from 'vue';
import { Head } from '@inertiajs/vue3';

const props = defineProps({
    domain: { type: Object, default: () => ({}) },
    brandName: { type: String, default: "Wärmepumpen-Ampel" },
    brandTagline: { type: String, default: "Erst prüfen, dann investieren" },
    brandAccent: { type: String, default: '#0EA5E9' },
    author: { type: Object, default: null },
    designVersion: { type: String, default: '' },
});

// Derive a few values that the chrome HTML references. Best-effort fallbacks
// for the values the chrome's masthead/footer reads.
const today = computed(() => new Date().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }));
const brandName = computed(() => props.brandName || props.domain?.settings?.brand_name || "Wärmepumpen-Ampel");
const brandTagline = computed(() => props.brandTagline || props.domain?.settings?.brand_tagline || "Erst prüfen, dann investieren");
const brandAccent = computed(() => props.brandAccent);
const author = computed(() => props.author);
const DESIGN_VERSION = computed(() => props.designVersion || '');


const fontsHref = "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700;800;900&display=swap";
</script>

<template>
    <div class="wpa" :style="`--wpa-accent:${brandAccent};`">
        <Head>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
            <link rel="stylesheet" :href="fontsHref" />
        </Head>
        <header class="wpa-nav">
                    <div class="wpa-nav__inner">
                        <a class="wpa-logo" href="/" aria-label="Startseite Wärmepumpen-Ampel">
                            <span class="wpa-logo__mark" aria-hidden="true">
                                <span class="wpa-logo__r"></span>
                                <span class="wpa-logo__y"></span>
                                <span class="wpa-logo__g"></span>
                            </span>
                            <span class="wpa-logo__txt">
                                <span class="wpa-logo__name">Wärmepumpen-Ampel</span>
                                <span class="wpa-logo__sub">{{ brandTagline }}</span>
                            </span>
                        </a>
                        <nav class="wpa-nav__links" aria-label="Sekundärnavigation">
                            <a href="#methodik">Methodik</a>
                            <a href="#faq">Fragen</a>
                            <a href="#wpa-stepper" class="wpa-nav__cta">Check starten</a>
                            <a href="/ratgeber">Ratgeber</a>
                        </nav>
                    </div>
                </header>
        <slot />
        <footer class="wpa-foot">
                    <div class="wpa-foot__inner">
                        <div class="wpa-foot__brand">
                            <span class="wpa-logo__mark" aria-hidden="true">
                                <span class="wpa-logo__r"></span>
                                <span class="wpa-logo__y"></span>
                                <span class="wpa-logo__g"></span>
                            </span>
                            <span>Wärmepumpen-Ampel &middot; {{ brandTagline }}</span>
                        </div>
                        <nav class="wpa-foot__links" aria-label="Footer">
                            <a href="/impressum">Impressum</a>
                            <a href="/datenschutz">Datenschutz</a>
                            <a href="/agb">AGB</a>
                            <a href="/widerruf">Widerruf</a>
                            <a href="/ratgeber">Ratgeber</a>
                        </nav>
                        <p class="wpa-foot__copy">&copy; {{ new Date().getFullYear() }} waermepumpenampel.de &middot; alle Berechnungen ohne Gewähr, ersetzen keine Fachplanung.</p>
                    </div>
                </footer>
    </div>
</template>

<style scoped>

/* v3 (editorial) — Linear/Stripe polish + Enpal/Thermondo conversion mechanics.
   wpa-* namespace preserved per AGENT.md. Mobile-first 375 base.
   - Fraunces display (italic for emphasis), Inter body
   - single teal accent #0F766E, warm cream surfaces
   - hairline borders only (rgba(20,17,13,0.08)), no heavy shadows
   - asymmetric editorial layouts, no 3-equal-card grid, no gradient result block
*/

.wpa {
    --wpa-ink: #14110d;
    --wpa-ink-2: #3f3a32;
    --wpa-mute: #807870;
    --wpa-cream: #f7f3ea;
    --wpa-cream-2: #efe8d8;
    --wpa-surface: #fdfbf5;
    --wpa-white: #ffffff;
    --wpa-line: rgba(20, 17, 13, 0.08);
    --wpa-line-strong: rgba(20, 17, 13, 0.16);
    --wpa-rot: #b91c1c;
    --wpa-gelb: #c2870b;
    --wpa-gruen: #0f766e;
    /* Warm coral CTA for funneling - high contrast vs teal/cream, eye-grab without sales-page sleaze */
    --wpa-cta: #ea580c;
    --wpa-cta-hover: #c2410c;
    --wpa-cta-glow: 0 8px 24px -8px rgba(234,88,12,0.45), 0 0 0 1px rgba(234,88,12,0.18);
    --wpa-ease: cubic-bezier(0.32, 0.72, 0, 1);

    background: var(--wpa-cream);
    color: var(--wpa-ink);
    font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
    font-size: 17px;
    line-height: 1.55;
    font-feature-settings: 'cv11', 'ss01';
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    min-height: 100dvh;
    padding-bottom: 88px;
    text-rendering: optimizeLegibility;
}
.wpa * { box-sizing: border-box; }
.wpa a { color: inherit; }
.wpa input, .wpa button, .wpa output { font-family: inherit; }
.wpa-num-tab { font-variant-numeric: tabular-nums; font-feature-settings: 'tnum'; }

/* ── Top nav ───────────────────────────────────────────────────────── */
.wpa-nav {
    background: rgba(247, 243, 234, 0.85);
    backdrop-filter: saturate(160%) blur(12px);
    -webkit-backdrop-filter: saturate(160%) blur(12px);
    border-bottom: 1px solid var(--wpa-line);
    position: sticky;
    top: 0;
    z-index: 30;
}
.wpa-nav__inner {
    max-width: 1180px;
    margin: 0 auto;
    padding: 14px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}
.wpa-logo { display: inline-flex; align-items: center; gap: 12px; text-decoration: none; color: var(--wpa-ink); }
.wpa-logo__mark { display: inline-flex; flex-direction: column; gap: 3px; padding: 5px 4px; background: var(--wpa-ink); border-radius: 6px; }
.wpa-logo__r, .wpa-logo__y, .wpa-logo__g { width: 7px; height: 7px; border-radius: 50%; }
.wpa-logo__r { background: #ef4444; }
.wpa-logo__y { background: #f5b945; }
.wpa-logo__g { background: #22c55e; }
.wpa-logo__txt { display: flex; flex-direction: column; line-height: 1.15; }
.wpa-logo__name { font-weight: 600; font-size: 15px; letter-spacing: -0.01em; }
.wpa-logo__sub { font-size: 11px; color: var(--wpa-mute); letter-spacing: 0.02em; }
.wpa-nav__links { display: none; align-items: center; gap: 28px; font-size: 14px; }
.wpa-nav__links a { text-decoration: none; color: var(--wpa-ink-2); transition: color 180ms var(--wpa-ease); }
.wpa-nav__links a:hover { color: var(--wpa-ink); }
.wpa-nav__cta {
    padding: 9px 16px;
    background: var(--wpa-ink);
    color: var(--wpa-cream) !important;
    border-radius: 999px;
    font-weight: 500;
}
.wpa-nav__cta:hover { background: var(--wpa-accent); color: var(--wpa-white) !important; }
@media (min-width: 720px) { .wpa-nav__links { display: inline-flex; } }

/* ── Hero ──────────────────────────────────────────────────────────── */
.wpa-hero { padding: 28px 20px 8px; position: relative; }
.wpa-hero__inner {
    max-width: 1180px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr;
    gap: 28px;
    align-items: stretch;
}
.wpa-hero__kicker {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    font-weight: 500;
    color: var(--wpa-ink-2);
    margin: 0 0 22px;
    padding: 8px 14px 8px 12px;
    background: var(--wpa-surface);
    border: 1px solid var(--wpa-line);
    border-radius: 999px;
    letter-spacing: 0.01em;
    width: max-content;
}
.wpa-hero__dot {
    width: 8px; height: 8px; border-radius: 50%;
    background: var(--wpa-accent);
    box-shadow: 0 0 0 4px rgba(15, 118, 110, 0.14);
}
.wpa-hero__title {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: clamp(40px, 8.5vw, 80px);
    line-height: 1.0;
    letter-spacing: -0.035em;
    margin: 0 0 18px;
    color: var(--wpa-ink);
    text-wrap: balance;
}
.wpa-hero__em {
    font-style: normal;
    font-weight: 700;
    color: var(--wpa-cta);
    letter-spacing: -0.035em;
}
.wpa-hero__sub {
    font-size: clamp(16px, 2.2vw, 19px);
    line-height: 1.5;
    color: var(--wpa-ink-2);
    margin: 0 0 28px;
    max-width: 56ch;
}
.wpa-hero__ctaRow { display: flex; flex-direction: column; gap: 12px; align-items: flex-start; }
.wpa-hero__small { font-size: 13px; color: var(--wpa-mute); }

/* Sample report card — the "what do I get" visual */
.wpa-hero__sample { display: flex; align-items: flex-start; justify-content: center; }
.wpa-sample {
    background: var(--wpa-white);
    border: 1px solid var(--wpa-line);
    border-radius: 20px;
    padding: 22px 22px 18px;
    width: 100%;
    max-width: 420px;
    box-shadow: 0 1px 0 rgba(255,255,255,0.5) inset, 0 24px 60px -32px rgba(20,17,13,0.18);
    position: relative;
}
.wpa-sample::before {
    content: '';
    position: absolute;
    inset: -1px;
    border-radius: 20px;
    background: linear-gradient(180deg, rgba(15,118,110,0.18), rgba(15,118,110,0) 40%);
    pointer-events: none;
    mask: linear-gradient(#000, transparent 30%);
    -webkit-mask: linear-gradient(#000, transparent 30%);
}
.wpa-sample__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 18px; }
.wpa-sample__tag { font-size: 11px; letter-spacing: 0.16em; text-transform: uppercase; color: var(--wpa-mute); font-weight: 500; }
.wpa-sample__dot { width: 9px; height: 9px; border-radius: 50%; }
.wpa-sample__dot--ok { background: var(--wpa-accent); box-shadow: 0 0 0 4px rgba(15,118,110,0.14); }
.wpa-sample__label { font-size: 12px; color: var(--wpa-mute); margin: 0 0 4px; }
.wpa-sample__big {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: clamp(40px, 8vw, 64px);
    letter-spacing: -0.045em;
    line-height: 1;
    margin: 0 0 18px;
    color: var(--wpa-ink);
    font-variant-numeric: tabular-nums;
}
.wpa-sample__bigPre { font-family: 'Inter', sans-serif; font-size: 14px; color: var(--wpa-mute); font-weight: 400; vertical-align: 0.55em; margin-right: 6px; letter-spacing: 0; }
.wpa-sample__bigCur { font-family: 'Inter', sans-serif; font-size: 22px; font-weight: 500; color: var(--wpa-cta); margin-left: 4px; vertical-align: 0.18em; }
.wpa-sample__row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    font-size: 13px;
    color: var(--wpa-ink-2);
    padding: 8px 0;
    border-top: 1px solid var(--wpa-line);
    margin: 0;
}
.wpa-sample__row span:last-child { font-weight: 600; color: var(--wpa-ink); }
.wpa-sample__row--strong span:last-child { color: var(--wpa-accent); }
.wpa-sample__foot {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid var(--wpa-line);
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: var(--wpa-mute);
}
.wpa-sample__foot svg { color: var(--wpa-accent); flex: none; }

/* Trust hairline strip */
.wpa-trust { margin-top: 36px; border-top: 1px solid var(--wpa-line); border-bottom: 1px solid var(--wpa-line); }
.wpa-trust__inner {
    max-width: 1180px;
    margin: 0 auto;
    padding: 18px 20px;
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
}
.wpa-trust__item { display: flex; justify-content: space-between; align-items: baseline; gap: 16px; font-size: 13px; }
.wpa-trust__k { color: var(--wpa-mute); letter-spacing: 0.12em; text-transform: uppercase; font-size: 11px; font-weight: 500; }
.wpa-trust__v { color: var(--wpa-ink); font-weight: 500; text-align: right; }

@media (min-width: 880px) {
    .wpa-hero { padding: 56px 32px 16px; }
    .wpa-hero__inner { grid-template-columns: 1.25fr 1fr; gap: 56px; }
    .wpa-hero__sample { justify-content: flex-end; align-items: center; }
    .wpa-sample { max-width: 440px; }
    .wpa-trust__inner { grid-template-columns: repeat(3, 1fr); gap: 28px; padding: 22px 32px; }
    .wpa-trust__item { flex-direction: column; align-items: flex-start; gap: 4px; }
    .wpa-trust__v { text-align: left; }
}

/* ── Stepper ───────────────────────────────────────────────────────── */
.wpa-step { padding: 48px 20px 64px; }
.wpa-step__inner { max-width: 820px; margin: 0 auto; }

.wpa-step__head {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 28px;
}
.wpa-step__counter {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 400;
    font-size: 34px;
    letter-spacing: -0.02em;
    color: var(--wpa-ink);
    line-height: 1;
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
}
.wpa-step__slash { color: var(--wpa-mute); font-weight: 300; margin: 0 2px; }
.wpa-step__titleWrap { min-width: 0; }
.wpa-step__kicker { font-size: 12px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--wpa-mute); margin: 0 0 4px; font-weight: 500; }
.wpa-step__title {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 500;
    font-size: clamp(24px, 4vw, 32px);
    letter-spacing: -0.02em;
    margin: 0;
    color: var(--wpa-ink);
    line-height: 1.1;
}
.wpa-step__prog {
    height: 3px;
    background: var(--wpa-line);
    border-radius: 1px;
    overflow: hidden;
    margin-top: 8px;
}
.wpa-step__progFill { display: block; height: 100%; background: var(--wpa-accent); transition: width 320ms var(--wpa-ease); }

.wpa-chapter { display: flex; flex-direction: column; gap: 36px; }
.wpa-field { display: flex; flex-direction: column; gap: 14px; }
.wpa-field__lbl {
    display: flex;
    align-items: baseline;
    gap: 12px;
    font-size: 16px;
    font-weight: 500;
    color: var(--wpa-ink);
    letter-spacing: -0.005em;
}
.wpa-field__num {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-size: 13px;
    color: var(--wpa-mute);
    font-weight: 400;
    font-variant-numeric: tabular-nums;
    min-width: 22px;
}

/* Radio cards — hairline, calm */
.wpa-radios { display: grid; gap: 10px; }
@media (min-width: 600px) {
    .wpa-radios--2 { grid-template-columns: 1fr 1fr; }
    .wpa-radios--3 { grid-template-columns: 1fr 1fr 1fr; }
    .wpa-radios--4 { grid-template-columns: 1fr 1fr; }
    .wpa-radios--5 { grid-template-columns: 1fr 1fr 1fr; }
}
.wpa-radio {
    appearance: none;
    background: var(--wpa-surface);
    border: 1px solid var(--wpa-line);
    border-radius: 12px;
    padding: 14px 16px;
    text-align: left;
    cursor: pointer;
    color: var(--wpa-ink);
    display: flex;
    align-items: flex-start;
    gap: 12px;
    min-height: 60px;
    transition: border-color 180ms var(--wpa-ease), background 180ms var(--wpa-ease), transform 100ms var(--wpa-ease);
    font: inherit;
}
.wpa-radio:hover { border-color: var(--wpa-line-strong); background: var(--wpa-white); }
.wpa-radio:active { transform: translateY(1px); }
.wpa-radio--on {
    border-color: var(--wpa-accent);
    background: var(--wpa-white);
    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.08);
}
.wpa-radio__bullet {
    width: 18px; height: 18px;
    border-radius: 50%;
    border: 1.5px solid var(--wpa-line-strong);
    display: inline-flex; align-items: center; justify-content: center;
    margin-top: 1px; flex: none;
    background: var(--wpa-white);
    transition: border-color 180ms var(--wpa-ease);
}
.wpa-radio--on .wpa-radio__bullet { border-color: var(--wpa-accent); }
.wpa-radio__bulletDot {
    width: 8px; height: 8px;
    border-radius: 50%;
    background: var(--wpa-accent);
    transform: scale(0);
    transition: transform 180ms var(--wpa-ease);
}
.wpa-radio--on .wpa-radio__bulletDot { transform: scale(1); }
.wpa-radio__body { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.wpa-radio__main { font-weight: 500; font-size: 15px; line-height: 1.3; }
.wpa-radio__hint { font-size: 13px; color: var(--wpa-mute); line-height: 1.4; }

/* Slider */
.wpa-slider {
    background: var(--wpa-surface);
    border: 1px solid var(--wpa-line);
    border-radius: 12px;
    padding: 18px 18px 14px;
}
.wpa-slider__row { display: flex; align-items: center; gap: 16px; }
.wpa-slider input[type=range] {
    flex: 1;
    -webkit-appearance: none;
    appearance: none;
    background: transparent;
    height: 28px;
    min-height: 44px;
}
.wpa-slider input[type=range]::-webkit-slider-runnable-track {
    height: 4px; background: var(--wpa-line-strong); border-radius: 2px;
}
.wpa-slider input[type=range]::-moz-range-track {
    height: 4px; background: var(--wpa-line-strong); border-radius: 2px;
}
.wpa-slider input[type=range]::-webkit-slider-thumb {
    -webkit-appearance: none; appearance: none;
    width: 22px; height: 22px; border-radius: 50%;
    background: var(--wpa-accent);
    border: 3px solid var(--wpa-white);
    box-shadow: 0 1px 6px rgba(15,118,110,0.4);
    margin-top: -9px;
    cursor: grab;
}
.wpa-slider input[type=range]::-moz-range-thumb {
    width: 22px; height: 22px; border-radius: 50%;
    background: var(--wpa-accent);
    border: 3px solid var(--wpa-white);
    box-shadow: 0 1px 6px rgba(15,118,110,0.4);
    cursor: grab;
}
.wpa-slider input[type=range]:focus { outline: none; }
.wpa-slider input[type=range]:focus::-webkit-slider-thumb { box-shadow: 0 0 0 4px rgba(15,118,110,0.18); }
.wpa-slider output {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-size: 22px;
    font-weight: 500;
    letter-spacing: -0.02em;
    color: var(--wpa-ink);
    min-width: 96px;
    text-align: right;
}
.wpa-slider__ticks {
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
    font-size: 11px;
    color: var(--wpa-mute);
    letter-spacing: 0.04em;
}

/* Text fields — Vercel-ish hairline */
.wpa-textfield {
    display: flex;
    align-items: center;
    gap: 12px;
    background: var(--wpa-surface);
    border: 1px solid var(--wpa-line);
    border-radius: 12px;
    padding: 4px 16px;
    transition: border-color 180ms var(--wpa-ease), background 180ms var(--wpa-ease);
}
.wpa-textfield:focus-within { border-color: var(--wpa-accent); background: var(--wpa-white); box-shadow: 0 0 0 3px rgba(15,118,110,0.08); }
.wpa-textfield input {
    flex: 1;
    border: 0;
    outline: 0;
    background: transparent;
    font-size: 18px;
    font-weight: 500;
    padding: 14px 0;
    color: var(--wpa-ink);
    min-height: 48px;
    font-variant-numeric: tabular-nums;
}
.wpa-textfield__unit { font-size: 13px; color: var(--wpa-mute); white-space: nowrap; }
.wpa-textfield--plz input { letter-spacing: 0.08em; }

/* Step nav */
.wpa-step__nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    padding-top: 24px;
    margin-top: 8px;
    border-top: 1px solid var(--wpa-line);
}

/* ── Buttons ───────────────────────────────────────────────────────── */
.wpa-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    font-family: inherit;
    font-weight: 500;
    font-size: 15px;
    letter-spacing: -0.005em;
    line-height: 1;
    padding: 14px 20px;
    min-height: 48px;
    border: 1px solid transparent;
    border-radius: 999px;
    cursor: pointer;
    transition: background 180ms var(--wpa-ease), color 180ms var(--wpa-ease), border-color 180ms var(--wpa-ease), transform 100ms var(--wpa-ease);
    text-decoration: none;
    white-space: nowrap;
}
.wpa-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.wpa-btn:active:not(:disabled) { transform: translateY(1px); }
.wpa-btn__arr { transition: transform 220ms var(--wpa-ease); }
.wpa-btn:not(:disabled):hover .wpa-btn__arr { transform: translateX(3px); }

/* Primary CTA = warm coral (high contrast against teal/cream, eye-magnet for funneling) */
.wpa-btn--primary {
    background: var(--wpa-cta);
    color: var(--wpa-white);
    box-shadow: var(--wpa-cta-glow);
    font-weight: 700;
    letter-spacing: -0.012em;
    transition: background 180ms var(--wpa-ease), box-shadow 200ms var(--wpa-ease), transform 180ms var(--wpa-ease);
}
.wpa-btn--primary:hover:not(:disabled) {
    background: var(--wpa-cta-hover);
    box-shadow: 0 12px 32px -8px rgba(234,88,12,0.55), 0 0 0 1px rgba(234,88,12,0.22);
    transform: translateY(-1px);
}
.wpa-btn--ghost { background: transparent; color: var(--wpa-ink); border-color: var(--wpa-line-strong); }
.wpa-btn--ghost:hover:not(:disabled) { background: var(--wpa-surface); border-color: var(--wpa-ink); }
.wpa-btn--lg { padding: 22px 32px; font-size: 19px; min-height: 60px; font-weight: 800; letter-spacing: -0.01em; }
.wpa-btn--full { width: 100%; }
.wpa-btn--stick { padding: 12px 20px; min-height: 48px; font-size: 15px; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; }
/* SHOUT class: aggressive German lead-gen CTA - caps, tight tracking, heavy weight */
.wpa-btn--shout {
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 900;
    font-size: 19px;
}
@media (min-width: 720px) {
    .wpa-btn--shout { font-size: 22px; letter-spacing: 0.09em; }
}
/* Metadata strip beneath CTAs (3 Min . kostenlos) */
.wpa-hero__meta, .wpa-form__meta {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: var(--wpa-mute);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    font-variant-numeric: tabular-nums;
    margin: 0;
}
.wpa-form__meta { text-align: center; margin-top: 8px; }
/* Prominent step counter chip (caps tracked) */
.wpa-step__chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 800;
    letter-spacing: 0.14em;
    color: var(--wpa-cta);
    margin: 0;
    padding: 6px 12px;
    background: rgba(234,88,12,0.08);
    border: 1px solid rgba(234,88,12,0.18);
    border-radius: 999px;
    width: max-content;
    font-variant-numeric: tabular-nums;
}

.wpa-back-link {
    background: transparent;
    border: 0;
    color: var(--wpa-mute);
    font-size: 14px;
    font-weight: 500;
    padding: 12px 8px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: color 180ms var(--wpa-ease);
    min-height: 44px;
}
.wpa-back-link:hover { color: var(--wpa-ink); }

/* ── Result ────────────────────────────────────────────────────────── */
.wpa-result {
    background: var(--wpa-white);
    border: 1px solid var(--wpa-line);
    border-radius: 20px;
    overflow: hidden;
    position: relative;
}
.wpa-result::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 4px;
    background: var(--wpa-accent);
}
.wpa-result__inner { padding: 36px 26px 28px; }

.wpa-money { padding-bottom: 24px; border-bottom: 1px solid var(--wpa-line); }
.wpa-money__label { font-size: 12px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--wpa-mute); margin: 0 0 14px; font-weight: 500; }
.wpa-money__value {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: clamp(58px, 12vw, 104px);
    line-height: 0.98;
    letter-spacing: -0.05em;
    margin: 0 0 12px;
    color: var(--wpa-ink);
    font-variant-numeric: tabular-nums;
    text-wrap: balance;
}
.wpa-money__value--alt { font-size: clamp(34px, 6vw, 52px); letter-spacing: -0.03em; }
.wpa-money__pre {
    font-family: 'Inter', sans-serif;
    font-size: clamp(16px, 2.4vw, 22px);
    color: var(--wpa-mute);
    font-weight: 400;
    letter-spacing: 0;
    margin-right: 10px;
    vertical-align: 0.55em;
}
.wpa-money__num { color: var(--wpa-accent); }
.wpa-money__cur {
    font-family: 'Inter', sans-serif;
    font-size: clamp(28px, 5vw, 44px);
    color: var(--wpa-accent);
    font-weight: 500;
    margin-left: 10px;
    vertical-align: 0.16em;
    letter-spacing: 0;
}
.wpa-money__hint { font-size: 14px; color: var(--wpa-ink-2); margin: 0 0 16px; }

.wpa-ampel {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 8px 14px 8px 12px;
    background: var(--wpa-cream);
    border: 1px solid var(--wpa-line);
    border-radius: 999px;
    font-size: 13px;
    font-weight: 500;
    color: var(--wpa-ink);
}
.wpa-ampel__pip { width: 10px; height: 10px; border-radius: 50%; }
.wpa-ampel--gruen .wpa-ampel__pip { background: var(--wpa-gruen); box-shadow: 0 0 0 3px rgba(15,118,110,0.18); }
.wpa-ampel--gelb .wpa-ampel__pip { background: var(--wpa-gelb); box-shadow: 0 0 0 3px rgba(194,135,11,0.18); }
.wpa-ampel--rot .wpa-ampel__pip { background: var(--wpa-rot); box-shadow: 0 0 0 3px rgba(185,28,28,0.18); }

.wpa-kpis {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0;
    margin: 24px 0 24px;
    border-top: 1px solid var(--wpa-line);
    border-bottom: 1px solid var(--wpa-line);
}
.wpa-kpis div {
    padding: 18px 18px;
    border-right: 1px solid var(--wpa-line);
    border-bottom: 1px solid var(--wpa-line);
}
.wpa-kpis div:nth-child(2n) { border-right: 0; }
.wpa-kpis div:nth-last-child(-n+2) { border-bottom: 0; }
.wpa-kpis div:nth-last-child(1):nth-child(2n + 1) { border-right: 0; }
.wpa-kpis dt { font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--wpa-mute); margin-bottom: 8px; font-weight: 500; }
.wpa-kpis dd {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    margin: 0;
    font-size: 24px;
    font-weight: 500;
    letter-spacing: -0.02em;
    color: var(--wpa-ink);
    line-height: 1;
}
.wpa-kpis dd span { font-family: 'Inter', sans-serif; font-size: 13px; color: var(--wpa-mute); font-weight: 400; margin-left: 4px; letter-spacing: 0; }
.wpa-kpis__win { color: var(--wpa-accent) !important; }

@media (min-width: 640px) {
    .wpa-kpis { grid-template-columns: repeat(4, 1fr); }
    .wpa-kpis div:nth-child(2n) { border-right: 1px solid var(--wpa-line); }
    .wpa-kpis div:nth-child(4n) { border-right: 0; }
    .wpa-kpis div { border-bottom: 0 !important; }
}

.wpa-method {
    margin-top: 8px;
    font-size: 13px;
    color: var(--wpa-ink-2);
}
.wpa-method summary {
    cursor: pointer;
    color: var(--wpa-ink);
    font-weight: 500;
    padding: 8px 0;
    list-style: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}
.wpa-method summary::after { content: '+'; font-size: 18px; color: var(--wpa-mute); transition: transform 180ms var(--wpa-ease); display: inline-block; }
.wpa-method[open] summary::after { transform: rotate(45deg); }
.wpa-method ul { margin: 8px 0 0; padding-left: 20px; }
.wpa-method li { margin: 6px 0; line-height: 1.55; }

/* Lead form */
.wpa-lead {
    padding: 32px 26px 28px;
    background: var(--wpa-cream);
    border-top: 1px solid var(--wpa-line);
}
.wpa-lead__head { margin-bottom: 24px; }
.wpa-lead__eyebrow { font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--wpa-accent); margin: 0 0 6px; font-weight: 600; }
.wpa-lead__title {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 500;
    font-size: clamp(22px, 3.4vw, 28px);
    letter-spacing: -0.02em;
    margin: 0 0 8px;
    line-height: 1.2;
    color: var(--wpa-ink);
    text-wrap: balance;
}
.wpa-lead__sub { font-size: 15px; color: var(--wpa-ink-2); margin: 0; line-height: 1.55; max-width: 56ch; }

.wpa-form { display: flex; flex-direction: column; gap: 16px; }
.wpa-form__grid { display: grid; grid-template-columns: 1fr; gap: 16px; }
@media (min-width: 560px) { .wpa-form__grid { grid-template-columns: 1fr 1fr; } }

.wpa-input { display: flex; flex-direction: column; gap: 6px; }
.wpa-input__lbl { font-size: 13px; font-weight: 500; color: var(--wpa-ink); }
.wpa-input__lbl em { font-weight: 400; font-style: italic; color: var(--wpa-mute); }
.wpa-input input {
    font-size: 16px;
    padding: 14px 16px;
    min-height: 50px;
    border: 1px solid var(--wpa-line-strong);
    border-radius: 10px;
    background: var(--wpa-white);
    color: var(--wpa-ink);
    transition: border-color 180ms var(--wpa-ease), box-shadow 180ms var(--wpa-ease);
}
.wpa-input input:focus { outline: 0; border-color: var(--wpa-accent); box-shadow: 0 0 0 3px rgba(15,118,110,0.12); }
.wpa-input--err input { border-color: var(--wpa-rot); }
.wpa-input__err { font-size: 12px; color: var(--wpa-rot); margin: 4px 0 0; font-weight: 500; }
.wpa-input__err--center { text-align: center; margin-top: 8px; }

.wpa-consent {
    display: grid;
    grid-template-columns: 22px 1fr;
    gap: 12px;
    align-items: flex-start;
    font-size: 12px;
    color: var(--wpa-mute);
    line-height: 1.55;
    padding: 16px 0 4px;
    border-top: 1px solid var(--wpa-line);
    margin-top: 4px;
}
.wpa-consent input {
    width: 20px; height: 20px;
    margin-top: 2px;
    accent-color: var(--wpa-accent);
    cursor: pointer;
}
.wpa-consent__txt { display: block; }
.wpa-consent--err { color: var(--wpa-rot); }

.wpa-done {
    padding: 48px 28px;
    background: var(--wpa-cream);
    border-top: 1px solid var(--wpa-line);
    text-align: center;
    color: var(--wpa-ink);
}
.wpa-done svg { color: var(--wpa-accent); margin-bottom: 16px; }
.wpa-done h3 {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 500;
    font-size: 26px;
    letter-spacing: -0.02em;
    margin: 0 0 10px;
}
.wpa-done p { font-size: 15px; color: var(--wpa-ink-2); margin: 0 auto; max-width: 46ch; }

.wpa-altRot {
    padding: 28px 26px;
    background: var(--wpa-cream);
    border-top: 1px solid var(--wpa-line);
}
.wpa-altRot h3 {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 500;
    font-size: 22px;
    letter-spacing: -0.02em;
    margin: 0 0 10px;
}
.wpa-altRot p { font-size: 14px; color: var(--wpa-ink-2); margin: 0 0 16px; }
.wpa-altRot__links { list-style: none; padding: 0; margin: 0 0 18px; display: flex; flex-direction: column; gap: 0; }
.wpa-altRot__links li { border-top: 1px solid var(--wpa-line); }
.wpa-altRot__links li:last-child { border-bottom: 1px solid var(--wpa-line); }
.wpa-altRot__links a {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 0;
    color: var(--wpa-ink);
    text-decoration: none;
    font-size: 14px;
    font-weight: 500;
    transition: color 180ms var(--wpa-ease);
}
.wpa-altRot__links a:hover { color: var(--wpa-accent); }
.wpa-altRot__arr { color: var(--wpa-mute); transition: transform 220ms var(--wpa-ease); }
.wpa-altRot__links a:hover .wpa-altRot__arr { transform: translateX(3px); color: var(--wpa-accent); }

.wpa-result__back { padding: 0 26px 22px; }

/* ── Methodology editorial ─────────────────────────────────────────── */
.wpa-meth {
    padding: 72px 20px 56px;
    background: var(--wpa-surface);
    border-top: 1px solid var(--wpa-line);
    border-bottom: 1px solid var(--wpa-line);
}
.wpa-meth__inner { max-width: 1080px; margin: 0 auto; }
.wpa-meth__head { margin-bottom: 36px; max-width: 780px; }
.wpa-meth__kicker { font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--wpa-accent); margin: 0 0 12px; font-weight: 600; }
.wpa-meth__h {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 500;
    font-size: clamp(28px, 5vw, 48px);
    letter-spacing: -0.025em;
    line-height: 1.08;
    margin: 0 0 14px;
    color: var(--wpa-ink);
    text-wrap: balance;
}
.wpa-meth__h em { font-style: normal; font-weight: 700; color: var(--wpa-cta); }
.wpa-meth__lead { font-size: 17px; color: var(--wpa-ink-2); margin: 0; line-height: 1.55; max-width: 56ch; }

.wpa-meth__list { list-style: none; padding: 0; margin: 0; }
.wpa-meth__row {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 20px;
    padding: 28px 0;
    border-top: 1px solid var(--wpa-line);
    align-items: start;
}
.wpa-meth__row:last-child { border-bottom: 1px solid var(--wpa-line); }
.wpa-meth__rowNum {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 400;
    font-size: 14px;
    color: var(--wpa-mute);
    letter-spacing: 0.04em;
    font-variant-numeric: tabular-nums;
    margin-top: 4px;
}
.wpa-meth__row h3 {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 500;
    font-size: clamp(20px, 2.6vw, 24px);
    letter-spacing: -0.015em;
    margin: 0 0 8px;
    line-height: 1.25;
}
.wpa-meth__row p { font-size: 15px; color: var(--wpa-ink-2); margin: 0; line-height: 1.55; max-width: 60ch; }
.wpa-meth__row p strong { color: var(--wpa-ink); font-weight: 600; }
.wpa-meth__rowTag {
    display: none;
    font-size: 11px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--wpa-mute);
    font-weight: 500;
    padding: 6px 12px;
    border: 1px solid var(--wpa-line);
    border-radius: 999px;
    background: var(--wpa-cream);
    align-self: start;
    margin-top: 6px;
    white-space: nowrap;
}
@media (min-width: 860px) {
    .wpa-meth__row { grid-template-columns: 56px 1fr auto; gap: 32px; }
    .wpa-meth__rowTag { display: inline-flex; align-items: center; }
}
.wpa-meth__fineprint { font-size: 13px; color: var(--wpa-mute); margin: 24px 0 0; line-height: 1.55; max-width: 64ch; }

/* ── FAQ ───────────────────────────────────────────────────────────── */
.wpa-faq { padding: 72px 20px 88px; }
.wpa-faq__inner { max-width: 880px; margin: 0 auto; }
.wpa-faq__head { margin-bottom: 32px; }
.wpa-faq__kicker { font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--wpa-accent); margin: 0 0 12px; font-weight: 600; }
.wpa-faq__h {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 500;
    font-size: clamp(28px, 5vw, 44px);
    letter-spacing: -0.025em;
    line-height: 1.1;
    margin: 0;
    text-wrap: balance;
}
.wpa-faq__h em { font-style: normal; font-weight: 700; color: var(--wpa-cta); }
.wpa-faq__list { margin: 0; padding: 0; }
.wpa-faq__item { padding: 22px 0; border-top: 1px solid var(--wpa-line); }
.wpa-faq__item:last-child { border-bottom: 1px solid var(--wpa-line); }
.wpa-faq__item dt {
    display: flex;
    gap: 14px;
    font-size: 17px;
    font-weight: 500;
    color: var(--wpa-ink);
    margin-bottom: 8px;
    line-height: 1.35;
    letter-spacing: -0.01em;
}
.wpa-faq__num {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 400;
    font-size: 13px;
    color: var(--wpa-mute);
    min-width: 24px;
    margin-top: 3px;
    font-variant-numeric: tabular-nums;
}
.wpa-faq__item dd { margin: 0 0 0 38px; font-size: 15px; color: var(--wpa-ink-2); line-height: 1.55; max-width: 60ch; }

/* ── Footer ────────────────────────────────────────────────────────── */
.wpa-foot { background: var(--wpa-ink); color: rgba(247,243,234,0.7); padding: 36px 20px 40px; }
.wpa-foot__inner { max-width: 1180px; margin: 0 auto; display: grid; grid-template-columns: 1fr; gap: 18px; }
.wpa-foot__brand { display: inline-flex; align-items: center; gap: 12px; color: var(--wpa-cream); font-size: 14px; font-weight: 500; }
.wpa-foot__links { display: flex; gap: 24px; font-size: 14px; }
.wpa-foot__links a { color: rgba(247,243,234,0.7); text-decoration: none; transition: color 180ms var(--wpa-ease); }
.wpa-foot__links a:hover { color: var(--wpa-cream); }
.wpa-foot__copy { font-size: 12px; color: rgba(247,243,234,0.45); margin: 0; line-height: 1.55; max-width: 64ch; }
@media (min-width: 720px) {
    .wpa-foot__inner { grid-template-columns: auto 1fr auto; align-items: center; gap: 24px; }
    .wpa-foot__copy { grid-column: 1 / -1; padding-top: 18px; border-top: 1px solid rgba(247,243,234,0.1); }
}

/* ── Sticky CTA ────────────────────────────────────────────────────── */
.wpa-stick {
    position: fixed;
    left: 12px; right: 12px;
    bottom: 12px;
    z-index: 40;
    background: var(--wpa-ink);
    color: var(--wpa-cream);
    border-radius: 14px;
    padding: 10px 12px 10px 18px;
    padding-bottom: max(10px, env(safe-area-inset-bottom, 10px));
    box-shadow: 0 18px 40px -16px rgba(20,17,13,0.5);
    border: 1px solid rgba(247,243,234,0.08);
}
.wpa-stick__inner { display: flex; align-items: center; justify-content: space-between; gap: 14px; max-width: 720px; margin: 0 auto; }
.wpa-stick__txt { display: flex; flex-direction: column; line-height: 1.2; font-size: 13px; min-width: 0; }
.wpa-stick__txt strong { font-size: 14px; font-weight: 600; color: var(--wpa-cream); }
.wpa-stick__txt span { font-size: 12px; color: rgba(247,243,234,0.6); }
.wpa-btn--stick { background: var(--wpa-accent); color: var(--wpa-white); border-color: var(--wpa-accent); flex: none; }
.wpa-btn--stick:hover:not(:disabled) { background: #0d645d; }
@media (min-width: 720px) { .wpa-stick { display: none; } }

/* ── Real photographic hero (WPA: coral accent frame) ──────────────── */
.wpa-hero-photo {
    margin: 0;
    width: 100%;
    max-width: 460px;
    aspect-ratio: 4 / 3;
    border-radius: 16px;
    overflow: hidden;
    position: relative;
    border: 1px solid rgba(234, 88, 12, 0.22);
    box-shadow: 0 8px 32px -12px rgba(20, 17, 13, 0.18);
    background: var(--wpa-white);
}
.wpa-hero-photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
.wpa-hero-photo__caption {
    position: absolute;
    top: 12px;
    left: 12px;
    margin: 0;
    pointer-events: none;
}
.wpa-hero-photo__chip {
    display: inline-block;
    background: rgba(255, 255, 255, 0.94);
    color: var(--wpa-ink);
    font-size: 11px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    font-weight: 600;
    padding: 6px 10px;
    border-radius: 999px;
    border: 1px solid rgba(20, 17, 13, 0.08);
    backdrop-filter: blur(6px);
}

.wpa-cite {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 18px;
    justify-content: center;
}
.wpa-cite__pill {
    display: inline-flex;
    align-items: center;
    border: 1px solid rgba(20, 17, 13, 0.12);
    border-radius: 999px;
    padding: 6px 12px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0.04em;
    color: var(--wpa-ink);
    background: var(--wpa-white);
}

.wpa-byline {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 18px auto 0;
    max-width: 460px;
    justify-content: center;
}
.wpa-byline__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #ea580c;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: 'Space Grotesk', 'Inter', sans-serif;
    font-weight: 600;
    font-size: 13px;
    letter-spacing: 0.02em;
}
.wpa-byline__txt { display: flex; flex-direction: column; line-height: 1.25; }
.wpa-byline__name { font-size: 13px; font-weight: 600; color: var(--wpa-ink); }
.wpa-byline__role { font-size: 12px; color: var(--wpa-mute); }

.wpa-meth-thumb {
    margin: 0 0 28px;
    max-width: 320px;
    border-radius: 14px;
    overflow: hidden;
    border: 1px solid rgba(234, 88, 12, 0.18);
    box-shadow: 0 6px 24px -16px rgba(20, 17, 13, 0.18);
}
.wpa-meth-thumb img { width: 100%; height: auto; aspect-ratio: 4 / 3; object-fit: cover; display: block; }
.wpa-meth-thumb figcaption {
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--wpa-mute);
    padding: 8px 12px;
    background: var(--wpa-white);
}

/* ── Reduced motion ────────────────────────────────────────────────── */
@media (prefers-reduced-motion: reduce) {
    .wpa *, .wpa *::before, .wpa *::after { transition-duration: 0.01ms !important; animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; }
}

</style>
